import SideMenuItem from '@components/SideMenu/SideMenuItem'
import { useMatch } from 'react-router-dom'
import SideMenuGroup from '@components/SideMenu/SideMenuGroup'
import SideMenuGroupItem from '@components/SideMenu/SideMenuGroupItem'
import Localization from '@localization/Index'
import { useContext, useState } from 'react'
import { ModuleIdEnum } from 'cs.node.utils/csapi/provisioning'
import SideMenuSection from './SideMenuSection'
import { allRoles, baseRoles, packagerRoles, warehouserRoles } from 'cs.node.utils/RoleUtils'
import { useMobileMediaQuery } from '@utils/MediaComponents'
import { AuthContext } from '@providers/AuthProvider'

type Props = {
    onClose?: ()=>unknown
}
const SideMenu = (props: Props) =>{
    const { onClose } = props 
    const isMobile = useMobileMediaQuery()
    const auth = useContext(AuthContext)

    const menuMatch = {
        Home: !!useMatch("/"),
        Shops: !!useMatch("/shops"),
        CreateShopChannel: !!useMatch("/shops/new/channel/:cs_shop_id"),
        CreateShop: !!useMatch("/shops/new/:cs_shop_id"),
        EditShopChannel: !!useMatch("/shops/channel/:cs_shop_id"),
        EditShop: !!useMatch("/shops/:cs_shop_id/:shop_id"),
        ShopsWarehousesBindings: !!useMatch("/shops/warehousesbindings"),
        Catalog: !!useMatch("/catalog"),
        Item: !!useMatch("/catalog/:sku_parent"),
        ValueBindings: !!useMatch("/listing/value-bindings"),
        ValueBindingsEditRoot: !!useMatch("/listing/value-bindings/root"),
        ValueBindingsEdit: !!useMatch("/listing/value-bindings/:id"),
        Orders: !!useMatch("/orders"),
        Order: !!useMatch("/orders/:id"),
        Shipments: !!useMatch("/shipments"),
        AvailabilityAdjustments: !!useMatch("/availability-adjustments"),
        Warehouses: !!useMatch("/warehouses"),
        WarehousesMovements: !!useMatch("/warehousesmovements"),
        Categories: !!useMatch("/categories"),
        Returns: !!useMatch("/returns"),
        Return: !!useMatch("/returns/:id"),
        BusinessConditions: !!useMatch("/businessconditions"),
        EditBusinessCondition: !!useMatch("/businessconditions/:id"),
        Packaging: !!useMatch("/packaging"),
        PackagingOrders: !!useMatch("/packaging/orders"),
        PackagingGroupages: !!useMatch("/packaging/groupages"),
        Reservations: !!useMatch("/reservations"),
        Queues: !!useMatch("/queues"),
        Queue: !!useMatch("/queues/:id"),
        QueueEntry: !!useMatch("/queues/:id/:entryId"),
        Customers: !! useMatch("/customers"),
        ListingExclusionRules: !!useMatch("/listing/exclusion-rules"),
        Modules: !!useMatch("/modules"),
        Reports: !!useMatch("/reports"),
        Report: !!useMatch("/reports/:id"),
        SettingsReports: !!useMatch("/settings/reports"),
        SettingsReportsCreate: !!useMatch("/settings/reports/new"),
        SettingsReportsEdit: !!useMatch("/settings/reports/:id"),
        Pricebooks: !!useMatch("/pricebooks"),
        Pricebook: !!useMatch("/pricebooks/:id"),
        Listing: !!useMatch("/listing"),
        ListingDetails: !!useMatch("/listing/:id"),
        AvailabilityBuckets: !!useMatch("/availability-buckets"),
        Dam: !!useMatch("/dam"),
        Users: !!useMatch("/users"),
        User: !!useMatch("/users/:id"),
        Export: !!useMatch("/export"),
    }

    const groupMatch = {
        Catalog: menuMatch.Catalog || menuMatch.AvailabilityAdjustments || menuMatch.Reservations || menuMatch.Item,
        Shops: menuMatch.Shops || menuMatch.CreateShopChannel || menuMatch.CreateShop || menuMatch.EditShop || menuMatch.EditShopChannel || menuMatch.ShopsWarehousesBindings,
        Warehouses: menuMatch.Warehouses || menuMatch.WarehousesMovements,
        Listing: menuMatch.ValueBindings || menuMatch.ValueBindingsEdit || menuMatch.ValueBindingsEditRoot || menuMatch.ListingExclusionRules || menuMatch.Listing || menuMatch.ListingDetails,
        Returns: menuMatch.Returns || menuMatch.Return,
        Prices: menuMatch.BusinessConditions || menuMatch.EditBusinessCondition || menuMatch.Pricebooks || menuMatch.Pricebook,
        Packaging: menuMatch.Packaging || menuMatch.PackagingOrders || menuMatch.PackagingGroupages,
        Reports: menuMatch.Reports || menuMatch.Report || menuMatch.SettingsReports || menuMatch.SettingsReportsCreate || menuMatch.SettingsReportsEdit,
        AvailabilityBuckets: menuMatch.AvailabilityBuckets,
    }

    const singleMatch = {
        Home: menuMatch.Home,
        Catalog: menuMatch.Catalog || menuMatch.Item,
        AvailabilityAdjustments: menuMatch.AvailabilityAdjustments,
        Categories: menuMatch.Categories,
        Orders: menuMatch.Orders || menuMatch.Order,
        Returns: menuMatch.Returns || menuMatch.Return,
        Shops: menuMatch.Shops || menuMatch.CreateShopChannel || menuMatch.CreateShop || menuMatch.EditShop || menuMatch.EditShopChannel,
        ShopsWarehousesBindings: menuMatch.ShopsWarehousesBindings,
        Shipments: menuMatch.Shipments,
        Warehouses: menuMatch.Warehouses,
        WarehousesMovements: menuMatch.WarehousesMovements,
        ValueBindings: menuMatch.ValueBindings || menuMatch.ValueBindingsEdit || menuMatch.ValueBindingsEditRoot,
        BusinessConditions: menuMatch.BusinessConditions || menuMatch.EditBusinessCondition,
        Packaging: menuMatch.Packaging,
        PackagingOrders: menuMatch.PackagingOrders,
        PackagingGroupages: menuMatch.PackagingGroupages,
        Reservations: menuMatch.Reservations,
        Queues: menuMatch.Queues || menuMatch.Queue || menuMatch.QueueEntry,
        ListingExclusionRules: menuMatch.ListingExclusionRules,
        Customers: menuMatch.Customers,
        Modules: menuMatch.Modules,
        Reports: menuMatch.Reports || menuMatch.Report,
        SettingsReports: menuMatch.SettingsReports || menuMatch.SettingsReportsCreate || menuMatch.SettingsReportsEdit,
        Pricebooks: menuMatch.Pricebooks || menuMatch.Pricebook,
        Listing: menuMatch.Listing,
        ListingDetails: menuMatch.ListingDetails,
        AvailabilityBuckets: menuMatch.AvailabilityBuckets,
        Dam: menuMatch.Dam,
        Users: menuMatch.Users || menuMatch.User,
        Export: menuMatch.Export
    }

    const currentGroupMatch = Object.entries(groupMatch).find(match=> match[1])?.[0] as keyof typeof groupMatch
    const currentSingleMatch = Object.entries(singleMatch).find(match=> match[1])?.[0] as keyof typeof singleMatch
    const [currentExpanded, setCurrentExpanded] = useState<string | undefined>(currentGroupMatch)

    const toggleExpanded = (match:string) =>{
        return setCurrentExpanded(prev=>{
            if(prev === match) return undefined
            return match
        })
    }

    if(isMobile) {
        return <div className='d-flex flex-column align-items-start justify-content-end'>
            <SideMenuItem onClose={onClose} roles={allRoles} name={Localization.MENU.DASHBOARD} icon="bi-speedometer" to="/" selected={currentSingleMatch === "Home"} />
            <SideMenuItem onClose={onClose} roles={warehouserRoles} name={Localization.MENU.ORDINI} icon="bi-card-checklist" to="/orders" selected={currentSingleMatch === "Orders"} />
            <SideMenuItem onClose={onClose} roles={warehouserRoles} name={Localization.MENU.LISTA_ARTICOLI} icon="bi-upc-scan" to="/catalog" selected={currentSingleMatch === 'Catalog'} />
            <SideMenuItem onClose={onClose} roles={baseRoles} name={Localization.MENU.SHOPS} icon="bi-cart" to="/shops" selected={currentSingleMatch === "Shops"} />
        </div>
    }

    return (<>
        <div className="d-flex flex-column position-fixed" style={{height: `calc(${CSS.supports("height", "100dvh") ? "100dvh" : "100vh"} - 140px)`}}>
            <div style={{width: '280px', paddingLeft: '0', paddingRight: '0', marginRight: "0", boxSizing: "content-box", overflowY: "auto"}}>
                <SideMenuItem roles={allRoles} name={Localization.MENU.DASHBOARD} icon="bi-speedometer" to="/" selected={currentSingleMatch === "Home"} />
                <SideMenuSection roles={allRoles} name={Localization.MENU.ATTIVITA} />
                <SideMenuItem roles={baseRoles} name={Localization.MENU.QUEUES} to="/queues" icon="bi-justify-left" selected={currentSingleMatch === "Queues"} />
                <SideMenuItem roles={baseRoles} name={Localization.MENU.EXPORT} to="/export" icon="bi-filetype-xlsx" selected={currentSingleMatch === "Export"} />
                <SideMenuGroup roles={baseRoles} name={Localization.MENU.LISTING} icon="bi-link-45deg" selected={currentGroupMatch === "Listing"} expanded={currentExpanded === "Listing"} onExpand={()=>{toggleExpanded('Listing')}}>
                    <SideMenuGroupItem roles={baseRoles} name={Localization.MENU.LISTING_ELENCO} to="/listing" selected={currentSingleMatch === "Listing"} />
                    <SideMenuGroupItem roles={baseRoles} name={Localization.MENU.ASSOCIAZIONE_VALORI} to="/listing/value-bindings" selected={currentSingleMatch === "ValueBindings"} />
                    <SideMenuGroupItem roles={baseRoles} name={Localization.MENU.REGOLE_ESCLUSIONE} to="/listing/exclusion-rules" selected={currentSingleMatch === "ListingExclusionRules"} />
                </SideMenuGroup>
                <SideMenuItem roles={warehouserRoles} name={Localization.MENU.ORDINI} icon="bi-card-checklist" to="/orders" selected={currentSingleMatch === "Orders"} />
                {auth.userData?.packaging_enabled === true &&<SideMenuGroup roles={packagerRoles} name={Localization.MENU.PACKAGING} icon="bi-box-seam" selected={currentGroupMatch === "Packaging"} expanded={currentExpanded === "Packaging"} onExpand={()=>{toggleExpanded('Packaging')}}>
                    <SideMenuGroupItem roles={packagerRoles} name={Localization.MENU.STAMPA_ETICHETTE} to="/packaging" selected={currentSingleMatch === "Packaging"} />
                    <SideMenuGroupItem roles={packagerRoles} name={Localization.MENU.ORDINI} to="/packaging/orders" selected={currentSingleMatch === "PackagingOrders"} />
                    <SideMenuGroupItem roles={packagerRoles} name={Localization.MENU.GROUPAGE} to="/packaging/groupages" selected={currentSingleMatch === "PackagingGroupages"} />
                </SideMenuGroup>}
                <SideMenuGroup roles={baseRoles} name={Localization.MENU.REPORT} icon="bi-file-earmark-text" selected={currentGroupMatch === "Reports"} expanded={currentExpanded === "Reports"} onExpand={()=>{toggleExpanded('Reports')}}>
                    <SideMenuGroupItem roles={baseRoles} name={Localization.MENU.LISTA} to="/reports" selected={currentSingleMatch === "Reports"} />
                    <SideMenuGroupItem roles={baseRoles} name={Localization.MENU.CONFIGURAZIONE_REPORT} to="/settings/reports" selected={currentSingleMatch === "SettingsReports"} />
                </SideMenuGroup>
                <SideMenuItem roles={baseRoles} name={Localization.MENU.RETURNS} icon="bi-arrow-repeat" to="/returns" selected={currentSingleMatch === "Returns"} />
                <SideMenuItem roles={warehouserRoles} name={Localization.MENU.SHIPMENTS} icon="bi-truck" to="/shipments" selected={currentSingleMatch === "Shipments"} />
                <SideMenuSection roles={warehouserRoles} name={Localization.MENU.GESTIONE} />
                <SideMenuItem roles={baseRoles} name={Localization.MENU.AVAILABILITY_BUCKET} icon="bi-bucket" to="/availability-buckets" selected={currentSingleMatch === "AvailabilityBuckets"} />
                <SideMenuGroup roles={warehouserRoles} name={Localization.MENU.CATALOGO} icon="bi-upc-scan" selected={currentGroupMatch === "Catalog"} expanded={currentExpanded === "Catalog"} onExpand={()=>{toggleExpanded('Catalog')}}>
                    <SideMenuGroupItem roles={warehouserRoles} name={Localization.MENU.LISTA_ARTICOLI} to="/catalog" selected={currentSingleMatch === 'Catalog'} />
                    <SideMenuGroupItem roles={baseRoles} name={Localization.MENU.AGGIORNAMENTO_DISPONIBILITA} to="/availability-adjustments" selected={currentSingleMatch === "AvailabilityAdjustments"} />
                    <SideMenuGroupItem roles={baseRoles} modules={[ModuleIdEnum.Warehouses]} name={Localization.MENU.RISERVE} to="/reservations" selected={currentSingleMatch === "Reservations"} />
                </SideMenuGroup>
                <SideMenuItem roles={baseRoles} name={Localization.MENU.CATEGORIES} icon="bi-share" to="/categories" selected={currentSingleMatch === "Categories"} />
                <SideMenuItem roles={baseRoles} name={Localization.MENU.CUSTOMERS} modules={[ModuleIdEnum.B2B]} to="/customers" icon="bi-people" selected={currentSingleMatch === "Customers"} />
                <SideMenuItem roles={baseRoles} name={Localization.MENU.DAM} to="/dam" icon="bi-file-earmark-text" selected={currentSingleMatch === "Dam"} />
                <SideMenuGroup roles={baseRoles} modules={[ModuleIdEnum.BusinessConditions]} name={Localization.MENU.GESTIONE_PREZZI} icon="bi-cash-stack" selected={currentGroupMatch === "Prices"} expanded={currentExpanded === "Prices"} onExpand={()=>{toggleExpanded('Prices')}}>
                    <SideMenuGroupItem roles={baseRoles} modules={[ModuleIdEnum.BusinessConditions]} name={Localization.MENU.CONDIZIONI_COMMERCIALI} to="/businessconditions" selected={currentSingleMatch === "BusinessConditions"} />
                    <SideMenuGroupItem roles={baseRoles} modules={[ModuleIdEnum.BusinessConditions]} name={Localization.MENU.LISTINI} to="/pricebooks" selected={currentSingleMatch === "Pricebooks"} />
                </SideMenuGroup>
                <SideMenuGroup roles={baseRoles} modules={[ModuleIdEnum.Warehouses]} name={Localization.MENU.MAGAZZINI} icon="bi-house" selected={currentGroupMatch === "Warehouses"} expanded={currentExpanded === "Warehouses"} onExpand={()=>{toggleExpanded('Warehouses')}}>
                    <SideMenuGroupItem roles={baseRoles} name={Localization.MENU.LISTA} to="/warehouses" selected={currentSingleMatch === "Warehouses"} />
                    <SideMenuGroupItem roles={baseRoles} name={Localization.MENU.WHS_MOVEMENTS} to="/warehousesmovements" selected={currentSingleMatch === "WarehousesMovements"} />
                </SideMenuGroup>
                <SideMenuItem roles={baseRoles} name={Localization.MENU.MODULI} to="/modules" icon="bi-grid" selected={currentSingleMatch === "Modules"} />
                <SideMenuGroup roles={baseRoles} name={Localization.MENU.SHOPS} icon="bi-cart" selected={currentGroupMatch === "Shops"} expanded={currentExpanded === "Shops"} onExpand={()=>{toggleExpanded('Shops')}}>
                    <SideMenuGroupItem roles={baseRoles} name={Localization.MENU.LISTA} to="/shops" selected={currentSingleMatch === "Shops"} />
                    <SideMenuGroupItem roles={baseRoles} name={Localization.MENU.ASSOCIAZIONE_MAGAZZINI} to="/shops/warehousesbindings" selected={currentSingleMatch === "ShopsWarehousesBindings"} />
                </SideMenuGroup>
                <SideMenuItem roles={baseRoles} name={Localization.MENU.UTENTI} to="/users" icon="bi-people-fill" selected={currentSingleMatch === "Users"} />
            </div>
            <div className="cs-sidebar-footer d-flex justify-content-end">
                {`CloudStore - v. ${process.env.APP_VERSION}`}
            </div>
        </div>
        </>
    )
}

export default SideMenu